<template>
  <div class="whistleblowingSystem">
    <div class="d-flex justify-content-end">
      <button
        class="ui-action-button"
        @click="save"
      >
        {{$t('global.saveChanges')}}
        <b-spinner class="ml-1" small label="loading" v-if="isUpdating"></b-spinner>
      </button>
    </div>

    <div class="row whistleblowingSystem__row">
      <b-col cols="12" sm="12" md="4" lg="3" class="whistleblowingSystem__row--col">
        <div class="input-container">
          <label class="text-black">{{ $t("integrityCompliance.integrity.form_title") }} (*)</label>
          <input
            type="text"
            id="title"
            name="title"
            v-model="category.title"
          />
          <div class="ui-input-error-msg">
            <span v-if="$v.category.title.$error">{{ $t("global.inputs.required") }}</span>
          </div>
        </div>
      </b-col>

      <b-col cols="12" class="whistleblowingSystem__row--col">
        <div class="wysiwyg-container">
          <label class="text-black">{{$t("integrityCompliance.whistleblowingSystem.intro")}}</label>
          <div class="wysiwyg">
            <Wysiwyg
              id="integrityDescription"
              ref="integrityDescription"
              :articleBody="category.originalDescription"
              :placeholder="$t('integrityCompliance.integrity.form_write_content')"
              :includeMediaContent="false"
              v-on:updateBody="category.description = $event"
            ></Wysiwyg>
            <div class="ui-input-error-msg">
              <span v-if="$v.category.description.$error">{{ $t("global.inputs.required") }}</span>
            </div>
          </div>
        </div>
      </b-col>

      <b-col cols="12" sm="12" md="6" lg="4" class="whistleblowingSystem__row--col">
        <label class="text-black">{{$t("integrityCompliance.whistleblowingSystem.howToMakeReport")}}</label>
        <FileUploader
            :key="fileUploaderKey"
            id="whistleblowingSystem_reportFile"
            :placeholder="$t('global.select')"
            type=".pdf"
            validate="pdf"
            :label="$t('global.browse')"
            :fileUrl="fileData.file_url"
            @setFile="setFile"
        />
        <div class="ui-input-error-msg">
          <span v-if="$v.fileData.file.$error">{{ $t("global.inputs.required") }}</span>
        </div>
      </b-col>
      </div>
  </div>
</template>

<script>
const Wysiwyg = () => import("@/components/Wysiwyg");
const FileUploader = () => import("@/components/FileUploader");

import API_SERVICE from "@/lib/api/IntegrityCompliance.js";
const api = new API_SERVICE();

import S3_Handler from "@/lib/api/s3_handler";
const s3 = new S3_Handler();

import { required, requiredIf } from "vuelidate/lib/validators";
import { validateBody } from '@/lib/utils.js';
import { integrityComplianceKey } from "@/lib/constants.js";

export default {
  inject: ['IC_getMainCategory'], // data from main category "Integrity"
  components: {
    Wysiwyg,
    FileUploader
  },
  data: function() {
    return {
      fileUploaderKey: new Date().getTime(),
      isUpdating: false,
      category: {
        id: null,
        title: '',
        description: {
          ops: []
        },
        originalDescription: {
          ops: []
        }
      },
      fileData: {
        file: null,
        file_url: ''
      }
    }
  },
  validations: {
    category: {
      title: {
        required
      },
      description: {
        isRequired: function(data) {
          return validateBody(data)
        }
      }
    },
    fileData: {
      file: {
        required: requiredIf(function() {
          if (this.fileData.file_url) {
            return false;
          } else {
            return true;
          }
        })
    }
    }
  },
  async mounted () {
    this.categoryData = this.IC_getMainCategory()
    this.category = { ...this.categoryData }
    this.category.originalDescription = this.categoryData.description
    await this.getCategoryFile();
  },
  methods: {
    async getCategoryFile () {
      try {
        this.isLoading = true;
        const categoryDetail = await api.getCategory(this.category.id);
        if (categoryDetail && categoryDetail.files) {
          const file = categoryDetail.files[0];
          const copyFileData = { ...this.fileData };
          this.fileData = {
            ...copyFileData,
            ...file
          }
        }
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('integrityCompliance.whistleblowingSystem.getError'),
          show: true,
          type: "error"
        });
      } finally {
        this.isLoading = false;
      }
    },
    async save() {
      this.category.title = this.category.title.trim()
      this.$v.$touch();
      if (this.$v.$error) return;

      try {
        this.isUpdating = true;
        await Promise.all([
          this.saveCategory(),
          this.saveFile()
        ])

        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('global.successSaved'),
          show: true,
          type: "success"
        });
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('integrityCompliance.whistleblowingSystem.error'),
          show: true,
          type: "error"
        });
      } finally {
        this.isUpdating = false;
      }
    },
    async saveCategory () {
      try {
        const params = {
          name: this.category.name,
          description: this.category.description,
          title: this.category.title
        }
        const response = await api.updateCategory(this.category.id, params)
        this.$emit('categoryUpdated', {
          id: this.category.id,
          ...response
        });
      } catch (error) {
        throw error;
      }
    },
    async saveFile () {
      const file = this.fileData.file;
      try {
        if (file) {
          const timestamp = Date.now();
          const filename = `IC/${this.categoryData.name}/${timestamp}.pdf`;
          const { Bucket, Key } = await s3.uploadFile(filename, file);
          const params = {
            id: file.id,
            name: filename,
            description: filename,
            srcBucket: Bucket,
            src: Key,
            category_id: this.categoryData.id
          }
          let response;
          if (this.fileData.id) {
            response = await api.updateDocument(this.fileData.id, params);
          } else {
            response = await api.createDocument(params);
          }

          if (response && response.id) {
            this.fileData = {
              ...response,
              file: null
            }
          }

          this.fileUploaderKey = `${integrityComplianceKey.whistleblowingSystem}_fileUploader_${new Date().getTime()}`
        }
      } catch (error) {
        throw error;
      }

    },
    setFile (file) {
      this.fileData.file = file
      if (!file) {
        this.fileUploaderKey = `${integrityComplianceKey.whistleblowingSystem}_fileUploader_${new Date().getTime()}`
      }
    }
  }
}
</script>

<style scoped lang="scss">
.whistleblowingSystem {
  &__row {
    &--col {
      margin-bottom: $global_padding;
    }
  }
}
</style>
